import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { isEmpty } from 'lodash-es';
import { TooltipComponent } from './tooltip.component';

const strategies = {
  top: {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -6
  },
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center'
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center'
  },
  bottom: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top'
  }
} as Record<string, ConnectedPosition>;

/**
 * In order to use this tooltip directive we need to import '~@angular/cdk/overlay-prebuilt.css' in our styles.css file
 */
@Directive({ selector: '[edsTooltip], [edsTooltipImage]' })
export class TooltipDirective implements OnDestroy {
  @Input('edsTooltip') text = '';
  @Input('edsTooltipImage') image = '';

  private readonly overlayRef: OverlayRef;
  private readonly tooltipPortal;

  constructor(
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly elementRef: ElementRef,
    private readonly overlay: Overlay
  ) {
    this.tooltipPortal = new ComponentPortal(TooltipComponent);

    const position = this.elementRef.nativeElement.getAttribute('data-tooltip-position') || 'auto';
    const positionStrategies =
      position !== 'auto' && strategies[position] ? [strategies[position]] : Object.values(strategies);

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef.nativeElement)
      .withPositions(positionStrategies);
    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  ngOnDestroy(): void {
    this.hide();
  }
  @HostListener('mouseenter')
  show() {
    if (isEmpty(this.text) && isEmpty(this.image)) {
      return;
    }
    const tooltipRef: ComponentRef<TooltipComponent> = this.overlayRef.attach(this.tooltipPortal);

    tooltipRef.instance.text = this.text;
    tooltipRef.instance.image = this.image;
  }

  @HostListener('mouseleave')
  hide() {
    this.overlayRef.detach();
  }

  @HostListener('touchend')
  touchend() {
    this.hide();
  }

  @HostListener('click')
  click() {
    this.hide();
  }
}
